import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  FaSearch, 
  FaLightbulb, 
  FaCode, 
  FaRocket,
  FaCheckCircle,
  FaBrain
} from "react-icons/fa";
import {
  headingAnimation,
  sectionBodyAnimation,
} from "../../../hooks/useAnimation";
import { BottomLine } from "../../../components";

const HowIWork = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  const [viewDiv, setViewDiv] = useState(false);

  useEffect(() => {
    if (inView) setViewDiv(true);
    else setViewDiv(false);
  }, [inView]);

  const steps = [
    {
      id: 1,
      title: "Discovery & Analysis",
      icon: <FaSearch className="text-2xl" />,
      description: "I begin by deeply understanding your business goals, target audience, and project requirements.",
      gradient: "from-violet-500 to-indigo-500"
    },
    {
      id: 2,
      title: "Planning & Strategy",
      icon: <FaBrain className="text-2xl" />,
      description: "Creating a detailed project roadmap, selecting the right tech stack, and planning the architecture.",
      gradient: "from-violet-500 to-indigo-500"
    },
    {
      id: 3,
      title: "Design & Prototyping",
      icon: <FaLightbulb className="text-2xl" />,
      description: "Designing user-friendly interfaces and creating interactive prototypes for your approval.",
      gradient: "from-violet-500 to-indigo-500"
    },
    {
      id: 4,
      title: "Development",
      icon: <FaCode className="text-2xl" />,
      description: "Building your application with clean, efficient code using modern technologies and best practices.",
      gradient: "from-violet-500 to-indigo-500"
    },
    {
      id: 5,
      title: "Testing & Quality",
      icon: <FaCheckCircle className="text-2xl" />,
      description: "Rigorous testing across all devices and browsers to ensure a flawless user experience.",
      gradient: "from-violet-500 to-indigo-500"
    },
    {
      id: 6,
      title: "Launch & Support",
      icon: <FaRocket className="text-2xl" />,
      description: "Deploying your project and providing ongoing maintenance and support.",
      gradient: "from-violet-500 to-indigo-500"
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-[#08060f] to-[#0c0a16] text-white relative overflow-hidden" id="process">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-violet-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
      {/* Section Heading */}
      <motion.div
        className="mb-12 relative z-10"
        initial="hidden"
        animate={viewDiv && "visible"}
        variants={headingAnimation}
      >
        
        <h1 className="text-4xl font-semibold text-center mb-3">
          How I <span className="text-violet-400">Work</span>
        </h1>
        <h3 className="text-center text-gray-400 text-lg tracking-wide">
          My Process
        </h3>
        <BottomLine />
      </motion.div>

      {/* Simple Process Cards */}
      <motion.div
        ref={ref}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 relative z-10"
        initial="hidden"
        animate={viewDiv && "visible"}
        variants={sectionBodyAnimation}
      >
        {steps.map((step, index) => (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 50 }}
            animate={viewDiv ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            whileHover={{
              y: -10,
              scale: 1.02,
              boxShadow: "0px 25px 50px rgba(168, 85, 247, 0.3)"
            }}
            className="p-5 sm:p-6 md:p-8 rounded-2xl bg-gradient-to-br from-violet-900/20 via-gray-900/20 to-indigo-900/10
                       backdrop-blur-lg border border-violet-500/20 shadow-xl
                       hover:shadow-violet-500/20 cursor-pointer flex flex-col h-full
                       group relative overflow-hidden"
          >
            {/* Icon Container with number badge on top-left corner */}
            <div className="relative z-10 mb-4 md:mb-6 inline-block">
              <div className="inline-flex items-center justify-center p-4 rounded-2xl
                            bg-gradient-to-br from-violet-600/30 to-indigo-600/30
                            border border-violet-500/30">
                <div className="text-white">
                  {step.icon}
                </div>
              </div>
              {/* Number badge — top-left corner of icon box */}
              <div className="absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full
                            bg-gradient-to-r from-violet-600 to-indigo-600
                            flex items-center justify-center text-white font-bold text-xs shadow-lg z-20">
                {index + 1}
              </div>
            </div>

            {/* Content */}
            <div className="relative z-10 flex-grow">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-white">
                {step.title}
              </h2>
              <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-4 md:mb-6">
                {step.description}
              </p>
            </div>

            {/* Gradient Border Bottom */}
            <div className="mt-4 md:mt-6 pt-3 md:pt-4 border-t border-violet-500/20">
              <div className={`h-1 w-full rounded-full bg-gradient-to-r ${step.gradient}`}></div>
            </div>

            {/* Hover Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600/0 via-violet-600/10 to-indigo-600/0 
                          opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </motion.div>
        ))}
      </motion.div>
      </div>
    </div>
  );
};

export default HowIWork;